import { Controller } from "@hotwired/stimulus";

// Controlador del wizard multi-step de la integracion SII (credenciales chilenas
// para emitir boletas electronicas). Replica el flujo de
// `SiitegrationOnboardingModal.tsx` + `SiitegrationModal.tsx` del frontend:
//
//   init → category → (steps-person | steps-company) → finish → submit
//
// La validacion del RUT se hace via fetch a /libredte/tax_situation
// (LibreDteController) que consulta el SII y retorna las actividades (giros)
// autorizadas. El submit final POSTea al controller generico via Turbo Stream.
//
// Sin OAuth ni callbacks externos: todo vive dentro del modal.
export default class extends Controller {
  static values = {
    step:        { type: String, default: "init" },
    category:    { type: Number, default: 0 },
    tipoDte:     { type: String, default: "" },
    libredteUrl: { type: String, default: "/libredte/tax_situation" }
  };

  static targets = [
    "stepInit", "stepCategory", "stepStepsPerson", "stepStepsCompany", "stepFinish",
    "continueFromCategory",
    "stepsCompanyTitleExenta", "stepsCompanyTitleAfecta",
    "finishTitleCompany", "finishTitlePerson",
    "badgeHonorarios", "badgeExenta", "badgeAfecta",
    "rutError", "rutErrorMessage",
    "rutInput", "changeRutLink",
    "giroField", "giroSelect",
    "personalFields", "regionSelect", "comunaSelect", "codigoDirOrigenInput",
    "categoriaInput", "tipoDteInput",
    "buttonSlot", "validateBtn", "submitBtn",
    "submitTemplate", "validateTemplate",
    "statesData"
  ];

  connect() {
    this.states = this.#parseStates();
    this.#syncHidden();
    this.#renderStep();
  }

  // -------- Navegacion entre steps --------

  goToCategory() {
    this.stepValue = "category";
    this.#renderStep();
  }

  goToStepsFromCategory() {
    if (!this.categoryValue) return;
    this.stepValue = this.categoryValue === 2 ? "steps-person" : "steps-company";
    this.#renderStep();
  }

  goToFinish() {
    this.stepValue = "finish";
    this.#renderStep();
  }

  goBack() {
    const map = {
      "category":       "init",
      "steps-person":   "category",
      "steps-company":  "category",
      "finish":         this.categoryValue === 2 ? "steps-person" : "steps-company"
    };
    const prev = map[this.stepValue];
    if (!prev) return;
    this.stepValue = prev;
    this.#renderStep();
  }

  // -------- Step 2: seleccion de categoria --------

  selectCategory(event) {
    const el = event.currentTarget;
    this.categoryValue = Number(el.dataset.categoria);
    this.tipoDteValue  = el.dataset.tipoDte || "";
    this.#syncHidden();
    this.#syncBadges();
    if (this.hasContinueFromCategoryTarget) {
      this.#setDisabled(this.continueFromCategoryTarget, !this.categoryValue);
    }
  }

  // -------- Step 4: validacion de RUT contra LibreDTE/SII --------

  // Enter en el RUT input dispara Validar (en vez de intentar submit del form).
  // Evita que la tecla Enter envie el form antes de haber validado.
  validateOnEnter(event) {
    event.preventDefault();
    if (this.validateBtnTarget.classList.contains("hidden")) return;
    this.validateRut(event);
  }

  async validateRut(event) {
    event.preventDefault();
    const rutRaw = (this.rutInputTarget.value || "").trim();
    if (!rutRaw) {
      this.#showRutError(this.#translateError("RUT_CANT_BE_BLANK"));
      return;
    }

    this.#hideRutError();
    this.#setValidateLoading(true);

    try {
      const rutWithDash = rutRaw.replace(/(.)$/, "-$1");
      const params = new URLSearchParams({
        rut:       rutWithDash,
        categoria: String(this.categoryValue || ""),
        tipoDte:   this.tipoDteValue || ""
      });

      const res  = await fetch(`${this.libredteUrlValue}?${params.toString()}`, {
        headers: { Accept: "application/json" }
      });
      const data = await res.json();

      if (data.success && Array.isArray(data.activities) && data.activities.length > 0) {
        this.#populateGiroSelect(data.activities);
        this.#showPostValidationUI();
      } else {
        const errors = Array.isArray(data.errors) ? data.errors.join(", ") : (data.errors || "RUT no válido");
        this.#showRutError(this.#translateError(errors));
      }
    } catch (_error) {
      this.#showRutError(this.#translateError("RUT_NO_VALID"));
    } finally {
      this.#setValidateLoading(false);
    }
  }

  changeRut(event) {
    event.preventDefault();
    this.rutInputTarget.readOnly = false;
    this.rutInputTarget.focus();

    // Limpiar giro + UI post-validacion + swap boton a Validar
    this.#clearGiroSelect();
    this.giroFieldTarget.classList.add("hidden");
    this.personalFieldsTarget.classList.add("hidden");
    this.changeRutLinkTarget.classList.add("hidden");
    this.#swapButton(this.validateTemplateTarget);
    this.#hideRutError();
  }

  giroChanged(event) {
    // El giro trae una categoria asociada; si difiere de la elegida en Step 2,
    // actualizamos para mantener consistencia (replica el onChange del frontend).
    const opt = event.target.options[event.target.selectedIndex];
    if (!opt) return;
    const cat = Number(opt.dataset.categoria || this.categoryValue);
    if (cat && cat !== this.categoryValue) {
      this.categoryValue = cat;
      this.#syncHidden();
      this.#syncFinishTitle();
      this.#syncPersonalFieldsVisibility();
    }
  }

  // -------- Cascade Region → Comunas (solo para categoria=2) --------

  regionChanged(event) {
    const region = event.target.value;
    const match  = this.states.find(s => s.region === region);
    const comunas = match?.comunas || [];

    // Limpiar y repoblar select de comunas
    const select = this.comunaSelectTarget;
    select.innerHTML = "";

    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = select.dataset.placeholder || "Seleccionar";
    select.appendChild(blank);

    comunas.forEach((c) => {
      const opt = document.createElement("option");
      opt.value = c.name;
      opt.dataset.code = c.code;
      opt.textContent = c.name;
      select.appendChild(opt);
    });

    this.codigoDirOrigenInputTarget.value = "";
  }

  comunaChanged(event) {
    const opt = event.target.options[event.target.selectedIndex];
    this.codigoDirOrigenInputTarget.value = opt?.dataset?.code || "";
  }

  // -------- Rendering / sync helpers --------

  #renderStep() {
    const stepTargets = {
      "init":          this.stepInitTarget,
      "category":      this.stepCategoryTarget,
      "steps-person":  this.stepStepsPersonTarget,
      "steps-company": this.stepStepsCompanyTarget,
      "finish":        this.stepFinishTarget
    };

    Object.entries(stepTargets).forEach(([name, el]) => {
      el.classList.toggle("hidden", name !== this.stepValue);
    });

    // La flecha de retroceso vive dentro de cada modal_pane (back_data). No
    // hace falta togglearla desde JS: el wrapper `stepXxx` la oculta junto al
    // step cuando cambia `stepValue`.

    // Sincronizar titulos dinamicos
    if (this.stepValue === "steps-company") this.#syncCompanyTitle();
    if (this.stepValue === "finish")        this.#syncFinishTitle();
  }

  #syncHidden() {
    if (this.hasCategoriaInputTarget) this.categoriaInputTarget.value = this.categoryValue || "";
    if (this.hasTipoDteInputTarget)   this.tipoDteInputTarget.value   = this.tipoDteValue  || "";
  }

  #syncBadges() {
    this.#toggle(this.badgeHonorariosTarget, this.categoryValue === 2);
    this.#toggle(this.badgeExentaTarget,     this.categoryValue === 1 && this.tipoDteValue === "exenta");
    this.#toggle(this.badgeAfectaTarget,     this.categoryValue === 1 && this.tipoDteValue === "afecta");
  }

  #syncCompanyTitle() {
    this.#toggle(this.stepsCompanyTitleExentaTarget, this.tipoDteValue === "exenta");
    this.#toggle(this.stepsCompanyTitleAfectaTarget, this.tipoDteValue === "afecta");
  }

  #syncFinishTitle() {
    this.#toggle(this.finishTitleCompanyTarget, this.categoryValue === 1);
    this.#toggle(this.finishTitlePersonTarget,  this.categoryValue !== 1);
    this.#syncBadges();
  }

  #syncPersonalFieldsVisibility() {
    // Replica `activitiesFromSII?.length > 0` del frontend: contar opciones reales
    // (excluyendo el blank). El select tiene >= 1 opcion (blank) tras populate.
    const hasActivities = this.giroSelectTarget.options.length > 1;
    const show = this.categoryValue === 2 && hasActivities;
    this.personalFieldsTarget.classList.toggle("hidden", !show);
  }

  #populateGiroSelect(activities) {
    const select = this.giroSelectTarget;
    select.innerHTML = "";

    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = select.dataset.placeholder || "Seleccionar";
    select.appendChild(blank);

    activities.forEach((item) => {
      const opt = document.createElement("option");
      opt.value = item.glosa;
      opt.dataset.giro = item.glosa;
      opt.dataset.categoria = String(item.categoria);
      opt.textContent = item.glosa;
      select.appendChild(opt);
    });
  }

  #clearGiroSelect() {
    const select = this.giroSelectTarget;
    select.innerHTML = "";
    const blank = document.createElement("option");
    blank.value = "";
    blank.textContent = "Seleccionar";
    select.appendChild(blank);
  }

  #showPostValidationUI() {
    this.giroFieldTarget.classList.remove("hidden");
    this.changeRutLinkTarget.classList.remove("hidden");
    this.rutInputTarget.readOnly = true;
    this.#swapButton(this.submitTemplateTarget);
    this.#syncPersonalFieldsVisibility();
  }

  // Replica la ternaria `{activitiesFromSII.length > 0 ? <Activar/> : <Validar/>}`
  // del frontend: solo UN boton existe en el DOM a la vez. Clona el content del
  // <template> correspondiente y reemplaza el slot — Stimulus re-wirea data-action
  // automaticamente al detectar el nuevo subtree (MutationObserver).
  #swapButton(templateTarget) {
    const node = templateTarget.content.firstElementChild?.cloneNode(true);
    if (!node) return;
    this.buttonSlotTarget.replaceChildren(node);
  }

  #setValidateLoading(loading) {
    if (!this.hasValidateBtnTarget) return;
    const btn = this.validateBtnTarget.querySelector("button") || this.validateBtnTarget;
    btn.disabled = loading;
    if (loading) btn.setAttribute("aria-busy", "true");
    else btn.removeAttribute("aria-busy");
  }

  #showRutError(message) {
    if (this.hasRutErrorMessageTarget) this.rutErrorMessageTarget.textContent = message;
    this.rutErrorTarget.classList.remove("hidden");
  }

  #hideRutError() {
    if (this.hasRutErrorMessageTarget) this.rutErrorMessageTarget.textContent = "";
    this.rutErrorTarget.classList.add("hidden");
  }

  #translateError(code) {
    const map = {
      "RUT_NO_VALID":                    "El RUT ingresado no es válido.",
      "RUT_CANT_BE_BLANK":               "El RUT no puede estar vacío.",
      "THERE_ARE_NO_SII_ACTIVITIES":     "No se encontraron actividades en el SII para este RUT.",
      "ACTIVITIES WITHOUT THIS CATEGORY": "El RUT no tiene actividades registradas para la categoría seleccionada.",
      "ACTIVITIES_WITHOUT_THIS_CATEGORY": "El RUT no tiene actividades registradas para la categoría seleccionada.",
      "CATEGORY_NOT_VALID":              "La categoría no es válida."
    };
    return map[code] || code || "Error al validar el RUT.";
  }

  #parseStates() {
    if (!this.hasStatesDataTarget) return [];
    try {
      return JSON.parse(this.statesDataTarget.textContent);
    } catch (_e) {
      return [];
    }
  }

  #toggle(el, show) {
    if (!el) return;
    el.classList.toggle("hidden", !show);
  }

  // Sincroniza atributo `disabled` + las clases que agrega `btn_to disabled: true`
  // (pointer-events-none, cursor-not-allowed, opacity-50). Sin esto, las clases
  // del render inicial sobreviven al cambio del atributo y bloquean el click.
  #setDisabled(el, disabled) {
    if (!el) return;
    el.disabled = disabled;
    el.classList.toggle("pointer-events-none", disabled);
    el.classList.toggle("cursor-not-allowed", disabled);
    el.classList.toggle("opacity-50", disabled);
  }
}
